/**
 * useDestructiveAuditWatcher — real-time security toast for super-admins.
 *
 * Polls /api/academy/admin/audit-log every 15s for destructive admin events
 * (user.delete, user.revoke_access, admin.2fa_disable, m365.revoke). When a
 * new event appears that wasn't initiated by the current super-admin, fires
 * a sonner toast so they have eyes on cross-admin activity without needing
 * to reload the dashboard.
 *
 * Why polling instead of SSE? Same MongoDB Atlas, same Cloudflare ingress —
 * a thin 15s poll is dead simple, survives proxy timeouts and is invisible
 * load (≤4 reqs/min). If we ever scale to many super-admins we can flip to
 * Mongo change-streams behind the same hook.
 */
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

const BACKEND_URL = (process.env.REACT_APP_API_BASE_URL || process.env.CRA_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);
const POLL_MS = 15_000;

const DESTRUCTIVE_ACTIONS = [
  'user.delete',
  'user.revoke_access',
  'admin.2fa_disable',
  'm365.revoke',
];

const ACTION_LABEL = {
  'user.delete':         'User deleted',
  'user.revoke_access':  'Access revoked',
  'admin.2fa_disable':   '2FA disabled',
  'm365.revoke':         'M365 access revoked',
};

export function useDestructiveAuditWatcher({ enabled, currentEmail }) {
  // High-water mark of the latest ts we've already shown — anything newer
  // is a fresh event. Initialised to "now" on first run so we never spam
  // the super-admin with backlog the moment they log in.
  const hwm = useRef(null);
  const me = (currentEmail || '').toLowerCase();

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const params = new URLSearchParams({
          actions: DESTRUCTIVE_ACTIONS.join(','),
          limit: '10',
        });
        const res = await fetch(`${BACKEND_URL}/api/academy/admin/audit-log?${params}`, {
          credentials: 'include',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const rows = data.rows || [];

        // First successful poll → just record the latest ts and bail. We
        // never toast historical events, only ones that happen after the
        // super-admin opened the dashboard.
        if (hwm.current === null) {
          hwm.current = rows[0]?.ts || new Date(0).toISOString();
          return;
        }

        // Newest first; iterate oldest → newest so toasts stack in order.
        const fresh = rows.filter((r) => r.ts > hwm.current).reverse();
        for (const r of fresh) {
          // Skip the current admin's own actions — they don't need a toast
          // for a button they just clicked.
          if ((r.actor_email || '').toLowerCase() === me) continue;
          const label = ACTION_LABEL[r.action] || r.action;
          const description = `${r.actor_email || 'someone'} → ${r.target || '—'}`;
          // For revoke-access events we offer a 1-click Undo that fires
          // /restore-access on the target. We resolve the user_id by
          // hitting the existing search endpoint — slow path is fine
          // because the toast button is only clicked occasionally.
          const opts = { description, duration: 12_000 };
          if (r.action === 'user.revoke_access' && r.target) {
            opts.action = {
              label: 'Undo',
              onClick: async () => {
                try {
                  const search = await fetch(
                    `${BACKEND_URL}/api/academy/admin/users?q=${encodeURIComponent(r.target)}`,
                    { credentials: 'include' },
                  );
                  if (!search.ok) throw new Error(`HTTP ${search.status}`);
                  const sj = await search.json();
                  const hit = (sj.rows || []).find((u) => (u.email || '').toLowerCase() === r.target.toLowerCase());
                  if (!hit) throw new Error('User not found');
                  const restore = await fetch(
                    `${BACKEND_URL}/api/academy/admin/users/${hit.id}/restore-access`,
                    { method: 'POST', credentials: 'include' },
                  );
                  if (!restore.ok) throw new Error(`HTTP ${restore.status}`);
                  toast.success('Access restored', { description: `${r.target} can sign in again.` });
                } catch (e) {
                  toast.error('Undo failed', { description: e.message || 'Unknown error' });
                }
              },
            };
          }
          toast.warning(label, opts);
        }
        if (rows.length) hwm.current = rows[0].ts;
      } catch {
        // Quietly swallow. Network blips happen; the next poll will
        // catch up. Surfacing this would just train the super-admin
        // to ignore yellow toasts.
      }
    };

    // Kick off immediately, then on interval.
    poll();
    timer = setInterval(poll, POLL_MS);
    return () => { cancelled = true; if (timer) clearInterval(timer); void cancelled; };
  }, [enabled, me]);
}
